import React, { useState } from 'react'
import { Button, Form, Input, InputNumber, Modal, Spin, Switch } from 'antd'
import { INationality } from '@app/interfaces/nationality'
import { AxiosResponse } from 'axios'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  createNationality,
  updateNationality,
} from '@app/store/actions/nationality-actions'
import NationalityRepository from '@api/repositories/nationality-repository'
import FormList from '@app/pages/reference-book-page-details/components/form-list/form-list'
import { openNotificationWithIcon } from '@app/shared/notification/notification'
import ModalFooter from '@app/shared/modal-footer/modal-footer'

interface props {
  nationality?: INationality
  edit?: boolean
}

const NationalityForm: React.FC<props> = ({ nationality, edit }) => {
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false)
  const [visible, setVisible] = useState<boolean>(false)
  const [form] = Form.useForm()
  const [t] = useTranslation()

  const showModal = () => setVisible(true)
  const hideModal = () => {
    setVisible(false)
    form.resetFields()
  }
  const dispatch = useDispatch()

  const onFinish = async (values: any) => {
    const languages = values.data.filter(
      (language) => language && language.name,
    )
    const newValues: INationality = {
      id: values.id,
      sorting: values.sorting,
      status: values.status,
      zagsId: values.zagsId,
      data: languages,
    }
    setConfirmLoading(true)
    const nationalitySubmitApi = nationality
      ? NationalityRepository.update
      : NationalityRepository.create

    await nationalitySubmitApi(newValues)
      .then((response: AxiosResponse<INationality>) => {
        if (nationality && response.status === 200) {
          setConfirmLoading(false)
          dispatch(updateNationality(response.data))
          form.resetFields()
          openNotificationWithIcon('success', t('success.updated'))
        }
        if (!nationality && response.status === 201) {
          setConfirmLoading(false)
          dispatch(createNationality(response.data))
          form.resetFields()
          openNotificationWithIcon('success', t('success.created'))
        }
        hideModal()
      })
      .catch(() => {
        setConfirmLoading(false)
        openNotificationWithIcon('error', t('errors.error'))
      })
  }

  return (
    <React.Fragment>
      <Button onClick={showModal} type={'primary'}>
        {edit ? (
          <i className="far fa-edit"> {t('general.edit')} </i>
        ) : (
          t('general.add')
        )}
      </Button>

      <Modal
        open={visible}
        title={edit ? t('nationality.edit') : t('nationality.add')}
        confirmLoading={confirmLoading}
        onCancel={hideModal}
        footer={[
          <React.Fragment key={1}>
            <ModalFooter
              confirm={confirmLoading}
              edit={edit}
              onCancel={hideModal}
              onSubmit={form.submit}
            />
          </React.Fragment>,
        ]}
      >
        <Spin spinning={confirmLoading}>
          <Form
            form={form}
            onFinish={onFinish}
            initialValues={{
              id: nationality?.id,
              sorting: nationality?.sorting || 0,
              status: edit ? nationality?.status : true,
              zagsId: edit && nationality?.zagsId,
            }}
            layout={'vertical'}
          >
            <Form.Item hidden={true} name={'id'}>
              <Input />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('errors.requiredMessage'),
                },
              ]}
              name={'status'}
              label={t('general.status')}
              valuePropName="checked"
            >
              <Switch
                disabled={confirmLoading}
                defaultChecked={nationality?.status === true}
              />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  type: 'number',
                  min: -2147483648,
                  max: 2147483648,
                  message: t('errors.minPriorityMessage'),
                },
              ]}
              label={t('general.sort')}
              name={'sorting'}
            >
              <InputNumber
                disabled={confirmLoading}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  type: 'number',
                  min: -2147483648,
                  max: 2147483648,
                  message: t('errors.minPriorityMessage'),
                },
              ]}
              label={t('general.zagsId')}
              name={'zagsId'}
            >
              <InputNumber
                disabled={confirmLoading}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <FormList confirmLoading={confirmLoading} object={nationality} />
          </Form>
        </Spin>
      </Modal>
    </React.Fragment>
  )
}

export default NationalityForm
